import type { ResolutionControllerTuning } from './tuning';

/**
 * DYNAMIC RESOLUTION — the controller (docs/ai/plans/dynamic-resolution.md §3).
 *
 * WHAT THIS IS AND WHY IT IS OURS. r185 ships the render-at-a-fraction lever
 * (`PassNode.setResolutionScale`), FSR 1, TAAU and RCAS. What three.js has never shipped, in any
 * release, is the thing that DECIDES the scale. That decision is the whole of the owner's ask —
 * "a range of screen resolutions and a range of screen scaling settings, we need them all to
 * work, including rendering at lower resolutions than the monitor for perf reasons" — because a
 * fixed fraction only works on the one machine it was tuned on.
 *
 * IT IS PURE, DELIBERATELY. No `three`, no DOM, no `performance.now()`, no `Math.random()`. Every
 * duration arrives as a `frameMs` argument and every decision is a function of the sample
 * windows, so the entire policy is unit-testable headlessly (`tests/resolution.test.ts`) and a
 * behaviour change shows up as a failing arithmetic golden rather than as a device report six
 * weeks later. It is NOT in `PURE_DIRS` (it is not a sim core and it may hold mutable state), but
 * it obeys the same import rules.
 *
 * THE ONE OUTPUT is `apply(scale, index)`, called only when the rung actually CHANGES. Today the
 * caller turns that into `renderer.setPixelRatio(output × scale)` — the compositor then upscales
 * to CSS size with hardware bilinear, zero extra GPU passes, identical on both backends. When a
 * post chain lands (plan stage D) the same callback becomes `pass.setResolutionScale(scale)` and
 * nothing in this file moves. That is the point of the seam.
 *
 * ── THE TWO MODES, AND WHY THE FALLBACK IS THE HARD PART ──
 *
 * `gpu` — real GPU time from `trackTimestamp` + `resolveTimestampsAsync`. Available on BOTH
 * backends (WebGL2 maps to `EXT_disjoint_timer_query_webgl2`), capability-detected, never
 * assumed: Firefox flag-gates it for Spectre and Safari only grew it in 17.
 *
 * `wallclock` — everything else, and the place a naive implementation goes wrong. With no
 * timestamp query the only signal is the rAF interval, and **vsync quantises it**: at 60 Hz you
 * read 16.7 / 33.3 / 50.0 ms and nothing between, so **headroom is unobservable**. A controller
 * that treats a 16.7 ms frame as "no headroom" can lower confidently but can never learn it is
 * safe to raise — it ratchets to the floor and stays there, which looks exactly like a bug and is
 * worse than not adapting at all. So attack is a miss FRACTION (a missed frame is unambiguous)
 * and decay is a slow backing-off PROBE: raise one rung, watch, and on a miss drop back and
 * double the wait. Same lesson as "a ratio between two rows survives where an absolute budget
 * does not."
 */

export type ResolutionMode = 'gpu' | 'wallclock' | 'off';

/** Everything the dev pane, the dev hook and the e2e need. Flat and JSON-serializable. */
export interface ResolutionStatus {
  enabled: boolean;
  mode: ResolutionMode;
  /** Current ladder rung (1.0 = native output resolution). */
  scale: number;
  index: number;
  /** Non-null when a human pinned a rung — the controller makes no automatic change. */
  pinned: number | null;
  /** Active freeze reasons, sorted. Non-empty ⇒ the controller is not deciding anything. */
  frozen: readonly string[];
  warmupLeft: number;
  cooldownLeft: number;
  displayPeriodMs: number;
  /** GPU-time budget per frame, ms. Only meaningful in `gpu` mode. */
  budgetMs: number;
  /** Median of the GPU-time window, ms (0 until a sample arrives). */
  gpuMs: number;
  /** Median rAF interval, ms. */
  frameMs: number;
  /** Rung changes since boot — a controller that never moves and one that thrashes look alike without this. */
  changes: number;
  /**
   * At the FLOOR rung and still over budget. The honest "resolution cannot fix this" tell — the
   * cost is CPU, or the machine is simply too slow, and driving lower buys nothing.
   */
  starved: boolean;
  /** Current wall-clock probe interval, seconds (grows on each failed probe). */
  probeSeconds: number;
}

export interface ResolutionController {
  status(): ResolutionStatus;
  /**
   * One frame. `frameMs` is the interval since the previous frame; `gpuMs` is a FRESH GPU-time
   * measurement or null when this frame produced none (timestamp resolution is asynchronous, so
   * most frames legitimately pass null — the caller must never re-send a value it already sent,
   * or the median window fills with copies of one measurement and stops reacting).
   */
  tick(frameMs: number, gpuMs: number | null): void;
  /** Reference-counted, by reason: overlapping freezes (cutscene during a world rebuild) compose. */
  freeze(reason: string): void;
  unfreeze(reason: string): void;
  setEnabled(on: boolean): void;
  /** Pin to the rung nearest `scale`, or null to hand control back. */
  pin(scale: number | null): void;
  /** Forget every sample and return to the top rung (a resize / DPI change invalidates the lot). */
  reset(): void;
}

/** Rolling median. Copy-then-sort: the windows are ~12 long, so this is not the hot path. */
export function median(values: readonly number[]): number {
  if (values.length === 0) return 0;
  const s = [...values].sort((a, b) => a - b);
  const mid = s.length >> 1;
  return s.length % 2 === 1 ? s[mid]! : ((s[mid - 1]! + s[mid]!) / 2);
}

/**
 * First rung at or below `target`, else the floor. DOWN-snapping (never up) is what makes the
 * ratio jump conservative: overshooting the drop is one blurry frame, undershooting it is a
 * second reallocation 30 frames later.
 */
export function snapDownIndex(ladder: readonly number[], target: number): number {
  for (let i = 0; i < ladder.length; i++) if (ladder[i]! <= target) return i;
  return ladder.length - 1;
}

/** Nearest rung to `scale` — used only by the human-facing pin. */
export function nearestIndex(ladder: readonly number[], scale: number): number {
  let best = 0;
  let bestD = Infinity;
  for (let i = 0; i < ladder.length; i++) {
    const d = Math.abs(ladder[i]! - scale);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

/**
 * Estimate the display's refresh period from rAF intervals. **Do not assume 16.7 ms** — 120 Hz
 * tablets and 144 Hz monitors are both in the target set, and a controller that thinks it has
 * 16.7 ms on a 144 Hz panel will never raise resolution.
 *
 * THE BAND IS THE TRICK. A dropped frame is an integer MULTIPLE of the period, so restricting
 * the histogram to intervals within 1.5× the fastest observed one excludes every multiple while
 * keeping the period itself. The mode of what remains is the period. Taking the plain mode over
 * all intervals would report 33.3 ms on a machine that is currently dropping every other frame —
 * which is precisely the machine that most needs the right answer.
 *
 * ⚠️ AND THE ESTIMATE IS CLAMPED TO `fallbackMs` FROM ABOVE — it may only ever discover a display
 * FASTER than the default 60 Hz, never a slower one. This is not conservatism, it is the one case
 * rAF genuinely cannot decide: a machine dropping every single frame on a 60 Hz panel produces a
 * trace that is *identical* to a healthy 30 Hz panel, and believing the 30 Hz reading would tell
 * the controller everything is fine at exactly the moment everything is not. Displays slower than
 * 60 Hz do not exist in the target set (60/90/120/144 Hz tablets and monitors); a machine at 50 Hz
 * simply sits one rung lower than it needs to, which is the harmless direction to be wrong in.
 */
export function estimateDisplayPeriodMs(intervals: readonly number[], fallbackMs: number): number {
  if (intervals.length < 30) return fallbackMs;
  const positive = intervals.filter((v) => v > 0).sort((a, b) => a - b);
  if (positive.length < 30) return fallbackMs;
  // The band's reference is the 5th PERCENTILE, not the minimum. A single fast frame at a
  // transition would otherwise set the band for the whole window — measured: one 8.5 ms frame in
  // a window of 17 ms frames made the controller believe it was on a 120 Hz panel, which moved
  // the budget under it while it was trying to settle.
  const fast = positive[Math.floor(0.05 * (positive.length - 1))]!;
  const band = fast * 1.5;
  // 0.5 ms buckets: finer than the difference between any two real refresh rates, coarser than
  // the jitter of a rAF timestamp.
  const buckets = new Map<number, number>();
  for (const v of intervals) {
    if (v <= 0 || v > band) continue;
    const key = Math.round(v * 2);
    buckets.set(key, (buckets.get(key) ?? 0) + 1);
  }
  let bestKey = -1;
  let bestN = 0;
  for (const key of [...buckets.keys()].sort((a, b) => a - b)) {
    const n = buckets.get(key)!;
    if (n > bestN) {
      bestN = n;
      bestKey = key;
    }
  }
  if (bestKey < 0) return fallbackMs;
  const est = bestKey / 2;
  // 240 Hz .. 25 Hz. Anything outside is a measurement artefact, not a display.
  if (!(est >= 4 && est <= 40)) return fallbackMs;
  return Math.min(est, fallbackMs);
}

/**
 * The GPU-time budget for the SCENE, per frame.
 *
 * `cpuReserveMs` is the measured, resolution-INDEPENDENT part of our frame (~7.8 ms: hiding the
 * canvas took a 20.4 ms frame to 7.8). Without subtracting it the controller would chase a target
 * the GPU alone can never reach.
 *
 * ⚠️ AND SUBTRACTING IT IS NOT ENOUGH — this is the one thing measurement changed about this
 * function. On a 120 Hz panel one refresh is 8.3 ms, our CPU floor is 7.8 ms, and the budget for
 * the GPU comes out at roughly zero. A controller handed a zero budget does not "give up"; it
 * drops a rung, measures, is still over, drops again, and ends at the floor — in a window it
 * could have rendered natively. That is not hypothetical: it is what the stage-C run did, 9 rung
 * changes and a permanent 0.5 scale, on a machine with headroom to spare.
 *
 * So the budget is taken against the first refresh MULTIPLE the CPU floor actually leaves room
 * inside. On a 120 Hz panel with a 7.8 ms floor that is 2 refreshes — target 60 fps, which is
 * both achievable and what the player gets anyway. `minBudgetMs` is the last resort when even
 * `maxRefreshDivisor` refreshes leave nothing; past that point resolution is not the problem, and
 * `starved` in the status is how the controller says so out loud.
 */
export function gpuBudgetMs(displayPeriodMs: number, t: ResolutionControllerTuning): number {
  for (let refreshes = 1; refreshes <= t.maxRefreshDivisor; refreshes++) {
    const budget = refreshes * displayPeriodMs * (1 - t.headroomFrac) - t.cpuReserveMs;
    if (budget >= t.minBudgetMs) return budget;
  }
  return t.minBudgetMs;
}

function push(ring: number[], value: number, cap: number): void {
  ring.push(value);
  if (ring.length > cap) ring.shift();
}

/**
 * Build a controller. `apply` is called ONLY on an actual rung change — the caller may assume a
 * call means "reallocate", which is exactly the cost the quantised ladder exists to bound.
 */
export function createResolutionController(
  tuning: ResolutionControllerTuning,
  apply: (scale: number, index: number) => void,
): ResolutionController {
  const ladder = tuning.ladder;
  const floor = ladder.length - 1;

  let enabled = tuning.enabled;
  let index = 0;
  let pinned: number | null = null;
  const frozen = new Set<string>();

  const gpuWindow: number[] = [];
  const frameWindow: number[] = [];
  const periodWindow: number[] = [];

  let warmupLeft = tuning.warmupFrames;
  let cooldownLeft = 0;
  let overBudgetRun = 0; // consecutive GPU samples above the panic threshold
  let underBudgetRun = 0; // consecutive frames entirely below the raise threshold
  let changes = 0;
  let gpuSeen = false;
  let gpuStaleFrames = 0;
  let displayPeriodMs = 1000 / tuning.defaultDisplayHz;

  // Wall-clock probe state. `probeMs` grows on every failed probe and resets on a clean one.
  let probeMs = tuning.probeSeconds * 1000;
  let cleanMs = 0;
  let probingMs = -1; // ≥0 while a probe is being verified

  /**
   * The ONE mutation point. Returns whether the rung actually moved — a clamped no-op must not
   * start a cooldown, and (in the wall-clock path) must not start verifying a probe that never
   * happened.
   *
   * Deliberately silent: a controller that logged every rung would drown the console on a machine
   * that is legitimately hunting. `changes` on `__fwg.state().resolution` is the tell.
   */
  const setIndex = (next: number): boolean => {
    const clamped = Math.max(0, Math.min(floor, next));
    if (clamped === index) return false;
    index = clamped;
    changes++;
    cooldownLeft = tuning.cooldownFrames;
    gpuWindow.length = 0;
    frameWindow.length = 0;
    overBudgetRun = 0;
    underBudgetRun = 0;
    apply(ladder[index]!, index);
    return true;
  };

  const clearWindows = (): void => {
    gpuWindow.length = 0;
    frameWindow.length = 0;
    overBudgetRun = 0;
    underBudgetRun = 0;
  };

  const budget = (): number => gpuBudgetMs(displayPeriodMs, tuning);

  const mode = (): ResolutionMode => {
    if (!enabled) return 'off';
    return gpuSeen ? 'gpu' : 'wallclock';
  };

  return {
    status: () => ({
      enabled,
      mode: mode(),
      scale: ladder[index]!,
      index,
      pinned,
      frozen: [...frozen].sort(),
      warmupLeft,
      cooldownLeft,
      displayPeriodMs,
      budgetMs: budget(),
      gpuMs: median(gpuWindow),
      frameMs: median(frameWindow),
      changes,
      starved: index === floor && gpuSeen && median(gpuWindow) > budget(),
      probeSeconds: probeMs / 1000,
    }),

    tick(frameMs, gpuMs) {
      // The display period is a property of the DISPLAY, so it keeps accumulating through
      // freezes and warm-up — those are the frames we have, and the ≤2×min band filters the
      // heavy ones out anyway.
      if (frameMs > 0 && frameMs < 1000) push(periodWindow, frameMs, tuning.displayPeriodSamples);
      if (periodWindow.length >= 30 && periodWindow.length % 15 === 0) {
        displayPeriodMs = estimateDisplayPeriodMs(periodWindow, 1000 / tuning.defaultDisplayHz);
      }

      if (gpuMs !== null && Number.isFinite(gpuMs) && gpuMs > 0) {
        gpuSeen = true;
        gpuStaleFrames = 0;
      } else if (gpuSeen) {
        // Timestamp resolution can stop (a WebGL disjoint event invalidates a whole batch). Fall
        // back honestly rather than steering on a frozen number.
        if (++gpuStaleFrames > 120) {
          gpuSeen = false;
          gpuWindow.length = 0;
        }
      }

      if (!enabled || pinned !== null) return;

      if (frozen.size > 0) {
        clearWindows();
        return;
      }
      if (warmupLeft > 0) {
        warmupLeft--;
        return;
      }
      if (cooldownLeft > 0) cooldownLeft--;

      if (frameMs > 0 && frameMs < 1000) push(frameWindow, frameMs, tuning.sampleWindow);
      if (gpuMs !== null && Number.isFinite(gpuMs) && gpuMs > 0) {
        push(gpuWindow, gpuMs, tuning.sampleWindow);
      }

      if (gpuSeen) tickGpu(gpuMs);
      else tickWallClock(frameMs);
    },

    freeze(reason) {
      frozen.add(reason);
    },
    unfreeze(reason) {
      if (!frozen.delete(reason)) return;
      if (frozen.size === 0) {
        // The pipeline is warm but whatever just closed (a cutscene, the editor, a world rebuild)
        // leaves a few expensive frames behind it. Shorter than a cold boot, still not zero.
        warmupLeft = Math.max(warmupLeft, tuning.resumeFrames);
        clearWindows();
      }
    },
    setEnabled(on) {
      enabled = on;
      if (!on) setIndex(0);
      else {
        warmupLeft = Math.max(warmupLeft, tuning.resumeFrames);
        clearWindows();
      }
    },
    pin(scale) {
      if (scale === null) {
        pinned = null;
        warmupLeft = Math.max(warmupLeft, tuning.resumeFrames);
        clearWindows();
        return;
      }
      const i = nearestIndex(ladder, scale);
      pinned = ladder[i]!;
      setIndex(i);
    },
    reset() {
      clearWindows();
      periodWindow.length = 0;
      warmupLeft = Math.max(warmupLeft, tuning.resumeFrames);
      cooldownLeft = 0;
      probeMs = tuning.probeSeconds * 1000;
      cleanMs = 0;
      probingMs = -1;
      if (pinned === null) setIndex(0);
    },
  };

  // ── GPU mode ────────────────────────────────────────────────────────────────────────────────
  function tickGpu(fresh: number | null): void {
    const b = budget();

    // PANIC first, and it ignores the cooldown: two consecutive samples at 1.5× budget is a
    // camera cut, a world rebuild or a stage that opened under us. Straight to the floor; the
    // probe/raise path walks back up.
    if (fresh !== null && fresh > b * tuning.panicFactor) {
      if (++overBudgetRun >= tuning.panicFrames) {
        setIndex(floor);
        return;
      }
    } else if (fresh !== null) {
      overBudgetRun = 0;
    }

    if (gpuWindow.length < tuning.sampleWindow || cooldownLeft > 0) return;
    const m = median(gpuWindow);

    if (m > b) {
      underBudgetRun = 0;
      // THE RATIO FORM. Cost is ~linear in pixel count and pixels go as scale², so the scale that
      // lands on budget is scale × sqrt(budget / measured). Snapped DOWN to a rung.
      const target = ladder[index]! * Math.sqrt(b / m);
      setIndex(snapDownIndex(ladder, target));
      return;
    }

    /*
     * DEAD ZONE + THE RUNG PREDICTION. The asymmetric thresholds are the hysteresis, but on their
     * own they are NOT enough, and the stage-C run proved it: one rung up multiplies GPU cost by
     * (nextScale / thisScale)² ≈ 1.32, so a frame sitting at 0.79 × budget passes "there is
     * headroom", raises, lands at 1.04 × budget, and is dropped straight back. Repeat forever —
     * measured at 147 rung changes in two minutes, on a machine that had headroom.
     *
     * So the question is not "is there headroom now?" but "would the NEXT rung still fit?".
     * Cost is ~linear in pixel count and pixels go as scale², which is the same relationship the
     * down path already uses; using it in both directions is what makes the controller settle.
     */
    if (index > 0 && m < b * tuning.raiseThresholdFrac) {
      const rungRatio = (ladder[index - 1]! / ladder[index]!) ** 2;
      if (m * rungRatio < b * tuning.raiseThresholdFrac) {
        if (++underBudgetRun >= tuning.raiseWindows * tuning.sampleWindow) setIndex(index - 1);
        return;
      }
    }
    underBudgetRun = 0;
  }

  // ── Wall-clock mode ─────────────────────────────────────────────────────────────────────────
  function tickWallClock(frameMs: number): void {
    const period = displayPeriodMs;
    const missAt = period * tuning.missFactor;
    const missed = frameMs > missAt;

    if (frameMs > period * tuning.wallPanicFactor) {
      if (++overBudgetRun >= tuning.panicFrames) {
        setIndex(floor);
        cleanMs = 0;
        probingMs = -1;
        return;
      }
    } else {
      overBudgetRun = 0;
    }

    // A probe under verification: any miss inside the verify window means the rung we just took
    // is not affordable. Drop back and WAIT LONGER next time — this is the whole reason the
    // fallback does not oscillate.
    if (probingMs >= 0) {
      if (missed) {
        setIndex(index + 1);
        probeMs = Math.min(probeMs * 2, tuning.probeMaxSeconds * 1000);
        cleanMs = 0;
        probingMs = -1;
        return;
      }
      probingMs += frameMs;
      if (probingMs >= tuning.probeVerifySeconds * 1000) {
        probeMs = tuning.probeSeconds * 1000; // it held — trust the machine again
        cleanMs = 0;
        probingMs = -1;
      }
      return;
    }

    if (missed) cleanMs = 0;
    else cleanMs += frameMs;

    if (frameWindow.length < tuning.sampleWindow || cooldownLeft > 0) return;

    let misses = 0;
    for (const v of frameWindow) if (v > missAt) misses++;
    if (misses / frameWindow.length > tuning.missFraction) {
      // Quantisation makes a sqrt-of-ratio meaningless here, but the NUMBER OF VSYNCS a frame is
      // taking is not quantisation noise — it is the honest integer we are missing by.
      const vsyncs = Math.max(1, Math.round(median(frameWindow) / period));
      setIndex(index + vsyncs);
      cleanMs = 0;
      return;
    }

    if (index > 0 && cleanMs >= probeMs && setIndex(index - 1)) probingMs = 0;
  }
}
